import { cn, Text } from '@bridebook/aura';
import { PlannerFace } from '@bridebook/web/components/ai-planner/planner-face';
import { aiPlannerRingBackground } from '@bridebook/web/lib/ai-planner/constants';
import { useTypedMessage } from '@bridebook/web/lib/ai-planner/hooks/use-typed-message';
import { m, type Transition, useReducedMotion } from 'motion/react';
import { type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styles from './planner-bubble.module.css';

// Size of the round button, and the width it grows to, ring included
const FAB_SIZE = 48;
const TILE_WIDTH = 266;
// Width of the gradient ring
const RING = 3;
// The tile grows first, then the words start
const TILE_WAIT_MS = 900;

// Grows out of the button with a light overshoot, and shrinks back the same way
const resizeTransition: Transition = {
  width: { duration: 0.32, ease: [0.26, 1.3, 0.42, 1] },
  height: { duration: 0.38, ease: [0.26, 1.2, 0.42, 1] },
  borderRadius: { duration: 0.32, ease: 'easeOut' },
};

type TileMessageProps = {
  message: string;
  onComplete: () => void;
};

/**
 * The tile's message, typed out after the tile has grown. The full text is laid out
 * invisibly underneath, so the tile has its final height at once and does not jump.
 * A shimmer runs along the bottom until the message is complete.
 */
const TileMessage = ({ message, onComplete }: TileMessageProps) => {
  const { isWaiting, typed, isComplete } = useTypedMessage(message, TILE_WAIT_MS);

  useEffect(() => {
    if (isComplete) onComplete();
  }, [isComplete, onComplete]);

  return (
    <>
      {!isComplete && <span aria-hidden className={styles.shimmer} />}
      {/* Screen readers get the message whole straight away */}
      <span role="status" className="sr-only">
        {message}
      </span>
      <Text
        as="span"
        variant="typo-sm-semibold"
        aria-hidden
        className="relative grid min-w-0 flex-1 break-words text-text-primary">
        <span className="invisible col-start-1 row-start-1">{message}</span>
        {!isWaiting && <span className={cn('col-start-1 row-start-1', styles.copy)}>{typed}</span>}
      </Text>
    </>
  );
};

type PlannerTileProps = {
  // What the planner says in the tile
  message: string;
  isShown: boolean;
  // The round button, shown whenever the tile is away
  children: ReactNode;
};

/**
 * The desktop entry button that grows into a greeting tile: the face moves to the corner
 * and the message types in beside it. When hidden it shrinks back to the round button.
 * The parent anchors it at the bottom-right, so it grows up and left. Needs a LazyMotion parent.
 */
export const PlannerTile = ({ message, isShown, children }: PlannerTileProps) => {
  const reduceMotion = useReducedMotion();
  // Keeps the tile's look while it shrinks back, until the round button takes over
  const [isShrinking, setIsShrinking] = useState(false);
  const [isWaving, setIsWaving] = useState(false);
  const [tileHeight, setTileHeight] = useState(FAB_SIZE);
  const contentRef = useRef<HTMLSpanElement>(null);
  const isTile = isShown || isShrinking;

  const wasShown = useRef(isShown);

  // Shrinks back only after a show, not when the button mounts round
  useEffect(() => {
    const wasShownBefore = wasShown.current;
    wasShown.current = isShown;
    if (isShown || !wasShownBefore) return;
    setIsShrinking(true);
    setIsWaving(false);
  }, [isShown]);

  // Takes the height from the content laid out at the tile's final width, so the
  // tile does not grow tall while it is still narrow
  useLayoutEffect(() => {
    if (isShown && contentRef.current) setTileHeight(contentRef.current.offsetHeight + RING * 2);
  }, [isShown, message]);

  const wave = useCallback(() => setIsWaving(true), []);

  return (
    <m.span
      className={cn(
        'relative block',
        // The ring shows through 3px of transparent border; the shimmer is clipped to the fill
        isTile &&
          'overflow-hidden border-3 border-transparent shadow-[0_5px_15px_rgba(0,0,0,0.05)]',
        isWaving && styles.hello,
      )}
      style={isTile ? aiPlannerRingBackground('var(--color-surface-tertiary)') : undefined}
      initial={false}
      animate={
        isShown
          ? { width: TILE_WIDTH, height: tileHeight, borderRadius: 16 }
          : { width: FAB_SIZE, height: FAB_SIZE, borderRadius: FAB_SIZE / 2 }
      }
      transition={reduceMotion ? { duration: 0 } : resizeTransition}
      onAnimationComplete={() => {
        if (!isShown) setIsShrinking(false);
      }}>
      {isShown ? (
        // 12px inside the ring on the sides, 8px at the top and bottom, the face and words centred
        <span
          ref={contentRef}
          className="flex items-center gap-2 px-3 py-2 text-left"
          style={{ width: TILE_WIDTH - RING * 2 }}>
          <PlannerFace size={31} className="relative shrink-0" />
          <TileMessage message={message} onComplete={wave} />
        </span>
      ) : isShrinking ? (
        <span className="flex size-full items-center justify-center">
          <PlannerFace size={32} />
        </span>
      ) : (
        children
      )}
    </m.span>
  );
};
